import { type Component, createMemo, createSignal, For, Show } from 'solid-js'
import { z } from 'zod'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Chip from '~/components/Chip'
import Form from '~/components/Form'
import Icon from '~/components/Icon'
import Input from '~/components/Input'
import Modal from '~/components/Modal'
import Select from '~/components/Select'
import useAlert from '~/components/useAlert'
import { useToast } from '~/components/useToast'
import { useForm } from '~/hooks/useForm'
import { type AIConnectionModelType, type AIConnectionType, aiService, type ProviderCatalogEntryType } from '~/services/ai.service'
import { resolveError } from '~/utils/error'
import { formatDateTime } from '~/utils/settings'

interface AIConnectionsManagerPropsType {
  catalog: ProviderCatalogEntryType[]
  connections: AIConnectionType[]
  /** Modelos de todas las conexiones, para decir cuántos tiene cada una. */
  models: AIConnectionModelType[]
  onChanged: () => void | Promise<void>
}

/** Cómo se pinta el resultado de la última comprobación. */
const ESTADOS = {
  ok: { label: 'Validada', color: 'success' as const, icon: 'material-symbols-light:check-circle-outline' },
  error: { label: 'Con error', color: 'danger' as const, icon: 'material-symbols-light:error-outline' },
  unverified: { label: 'Sin probar', color: 'default' as const, icon: 'material-symbols-light:help-outline' }
}

/**
 * Las cuentas de proveedor de la empresa.
 *
 * Una conexión es UNA cuenta, no "el proveedor": se pueden tener varias del
 * mismo servicio ("Gemini producción", "Gemini del cliente A") y lo que las
 * distingue es el nombre. Antes sólo se admitía una por proveedor, así que
 * configurar una segunda cuenta machacaba la primera en silencio.
 */
const AIConnectionsManager: Component<AIConnectionsManagerPropsType> = props => {
  const toast = useToast()
  const alert = useAlert()

  const [showModal, setShowModal] = createSignal(false)
  const [editing, setEditing] = createSignal<AIConnectionType | null>(null)
  const [testing, setTesting] = createSignal<number | null>(null)

  // Todos los proveedores del catálogo, siempre: tener ya una cuenta de Gemini
  // no es motivo para no poder añadir otra.
  const providerOptions = () => props.catalog.map(p => ({ value: p.name, title: p.label, subtitle: p.notes ?? undefined }))

  const modelCount = createMemo(() => {
    const cuenta = new Map<number, number>()
    for (const m of props.models) {
      if (!m.available) continue
      cuenta.set(m.connectionId, (cuenta.get(m.connectionId) ?? 0) + 1)
    }
    return cuenta
  })

  // Qué exige cada proveedor sale del catálogo, así que la regla se evalúa en el
  // momento de validar y no al construir el esquema.
  const connectionSchema = z
    .object({
      provider: z.string().min(1, 'Elige un proveedor'),
      label: z.string().trim().min(1, 'Ponle un nombre para distinguirla'),
      apiKey: z.string().default(''),
      baseUrl: z.string().trim().default('')
    })
    .superRefine((data, ctx) => {
      const def = props.catalog.find(p => p.name === data.provider)
      if (!def) return

      if (def.requiresBaseUrl && !data.baseUrl && !def.defaultBaseUrl) {
        ctx.addIssue({ code: 'custom', path: ['baseUrl'], message: `${def.label} necesita una Base URL` })
      }
      if (def.requiresApiKey && !data.apiKey && !editing()?.hasApiKey) {
        ctx.addIssue({ code: 'custom', path: ['apiKey'], message: `${def.label} necesita una API Key` })
      }
    })

  const form = useForm({
    schema: connectionSchema,
    initialValues: () => {
      const c = editing()
      return {
        provider: c?.provider ?? '',
        label: c?.label ?? '',
        apiKey: '',
        baseUrl: c?.baseUrl ?? ''
      }
    },
    onSubmit: async values => {
      const existente = editing()

      if (existente) {
        // El proveedor no viaja: cambiarlo dejaría a los agentes que usan esta
        // cuenta hablando con otro servicio sin enterarse.
        await aiService.updateConnection(existente.id, {
          label: values.label,
          ...(values.apiKey ? { apiKey: values.apiKey } : {}),
          ...(values.baseUrl ? { baseUrl: values.baseUrl } : {})
        })
      } else {
        await aiService.createConnection({
          provider: values.provider,
          label: values.label,
          ...(values.apiKey ? { apiKey: values.apiKey } : {}),
          ...(values.baseUrl ? { baseUrl: values.baseUrl } : {})
        })
      }

      toast.addToast({ title: existente ? 'Conexión guardada' : 'Conexión creada', type: 'success' })
      setShowModal(false)
      await props.onChanged()
    },
    onError: err => toast.addToast({ title: resolveError(err), type: 'danger' })
  })

  const providerDef = () => props.catalog.find(p => p.name === form.values().provider)

  /**
   * Nombre sugerido al elegir proveedor: su etiqueta, y si ya existe una así, con
   * un número detrás. Evita el 422 por nombre repetido antes de que ocurra.
   */
  const suggestLabel = (provider: string): string => {
    const base = props.catalog.find(p => p.name === provider)?.label ?? provider
    const usados = new Set(props.connections.map(c => c.label))
    if (!usados.has(base)) return base
    let n = 2
    while (usados.has(`${base} ${n}`)) n++
    return `${base} ${n}`
  }

  const openCreate = () => {
    setEditing(null)
    const first = props.catalog[0]
    form.reset({ provider: first?.name ?? '', label: first ? suggestLabel(first.name) : '', apiKey: '', baseUrl: '' })
    setShowModal(true)
  }

  const openEdit = (c: AIConnectionType) => {
    setEditing(c)
    setShowModal(true)
  }

  /** Al cambiar de proveedor se resugiere el nombre, salvo que ya lo hayan tocado. */
  const onProviderChange = (value: string) => {
    form.setValue('provider', value)
    const actual = String(form.values().label ?? '')
    const eraSugerido = props.catalog.some(p => actual === p.label || actual.startsWith(`${p.label} `))
    if (!actual || eraSugerido) form.setValue('label', suggestLabel(value))
  }

  const test = async (c: AIConnectionType) => {
    setTesting(c.id)
    try {
      const resultado = await aiService.testConnection(c.id)
      toast.addToast({
        title: resultado.ok ? `Conexión correcta · ${resultado.models} modelos` : 'La conexión falló',
        description: resultado.error ?? undefined,
        type: resultado.ok ? 'success' : 'danger'
      })
      await props.onChanged()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    } finally {
      setTesting(null)
    }
  }

  const remove = async (c: AIConnectionType) => {
    const ok = await alert.confirmDelete({
      title: `¿Eliminar "${c.label}"?`,
      message: 'Las capacidades y los agentes que la usen se quedarán sin cuenta asignada y habrá que reconfigurarlos.'
    })
    if (!ok) return
    try {
      await aiService.deleteConnection(c.id)
      toast.addToast({ title: 'Conexión eliminada', type: 'success' })
      await props.onChanged()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    }
  }

  const providerLabel = (name: string) => props.catalog.find(p => p.name === name)?.label ?? name

  return (
    <Card>
      <Card.Title>
        <div class="flex items-center justify-between gap-2">
          <div class="flex items-center gap-2">
            <Icon icon="material-symbols-light:key-outline" size={20} />
            <div>
              <h2 class="text-xl font-semibold text-foreground">Conexiones</h2>
              <p class="text-sm text-foreground-subtle">Cada cuenta con su clave. Puedes tener varias del mismo proveedor.</p>
            </div>
          </div>
          <Button size="sm" prependIcon="material-symbols-light:add-circle-outline" onClick={openCreate}>
            Añadir
          </Button>
        </div>
      </Card.Title>

      <Card.Body>
        <Show
          when={props.connections.length}
          fallback={<p class="text-sm text-foreground-subtle">Aún no hay ninguna. Añade la de tu proveedor (OpenAI, Gemini, Ollama…) y luego elígela en cada capacidad.</p>}
        >
          <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
            <For each={props.connections}>
              {c => (
                <div class="rounded-lg border border-border p-3 flex flex-col gap-2">
                  <div class="flex items-start justify-between gap-2">
                    <div class="min-w-0">
                      <p class="font-medium text-foreground truncate" title={c.label}>
                        {c.label}
                      </p>
                      <p class="text-xs text-foreground-subtle truncate">{providerLabel(c.provider)}</p>
                    </div>
                    <Chip size="sm" variant="soft" color={ESTADOS[c.status].color} icon={ESTADOS[c.status].icon} class="shrink-0 whitespace-nowrap">
                      {ESTADOS[c.status].label}
                    </Chip>
                  </div>

                  <Show when={c.baseUrl}>
                    <p class="text-xs text-foreground-subtle truncate" title={c.baseUrl ?? ''}>
                      {c.baseUrl}
                    </p>
                  </Show>

                  <div class="text-xs text-foreground-subtle space-y-0.5">
                    <p>
                      <Show when={c.hasApiKey} fallback="Sin clave">
                        Clave {c.apiKey}
                      </Show>
                      <Show when={(modelCount().get(c.id) ?? 0) > 0}> · {modelCount().get(c.id)} modelos</Show>
                    </p>
                    <Show when={c.lastValidatedAt}>
                      <p>Probada el {formatDateTime(c.lastValidatedAt)}</p>
                    </Show>
                  </div>

                  <Show when={c.status === 'error' && c.lastError}>
                    <p class="text-xs text-danger break-words" title={c.lastError ?? ''}>
                      {c.lastError}
                    </p>
                  </Show>

                  <div class="flex items-center gap-1 mt-auto pt-1">
                    <Button size="sm" variant="soft" color="info" prependIcon="material-symbols-light:cable" disabled={testing() === c.id} onClick={() => test(c)}>
                      {testing() === c.id ? 'Probando…' : 'Probar'}
                    </Button>
                    <Button size="sm" variant="soft" color="default" prependIcon="material-symbols-light:edit-outline" onClick={() => openEdit(c)}>
                      Editar
                    </Button>
                    <Button size="sm" variant="text" color="danger" icon="material-symbols-light:delete-outline" iconSize={18} onClick={() => remove(c)} aria-label="Eliminar conexión" />
                  </div>
                </div>
              )}
            </For>
          </div>
        </Show>
      </Card.Body>

      <Modal isOpen={showModal()} onClose={() => setShowModal(false)}>
        <Modal.Header>
          <h2 class="text-xl font-bold text-foreground">{editing() ? 'Editar conexión' : 'Nueva conexión'}</h2>
        </Modal.Header>
        <Modal.Body>
          <Form id="connection-form" controller={form}>
            <div class="space-y-3">
              <div>
                <Select
                  name="provider"
                  label="Proveedor"
                  value={(form.values().provider as string) ?? ''}
                  options={providerOptions()}
                  disabled={Boolean(editing())}
                  onChange={value => onProviderChange(String(value ?? ''))}
                />
                <Show when={editing()}>
                  <p class="text-xs text-foreground-subtle mt-1">El proveedor no se puede cambiar: para otro servicio, crea otra conexión.</p>
                </Show>
                <Show when={providerDef()?.notes}>
                  <p class="text-xs text-foreground-subtle mt-1">{providerDef()?.notes}</p>
                </Show>
              </div>

              <Input name="label" label="Nombre" placeholder="p.ej. Gemini producción" helperText="Es lo que verás al asignar esta cuenta a un agente." />

              <Show when={providerDef()?.requiresApiKey}>
                <Input name="apiKey" label="API Key" type="password" placeholder={editing()?.hasApiKey ? 'Guardada — deja vacío para conservarla' : 'Tu clave del proveedor'} />
              </Show>

              <Show when={providerDef()?.supportsBaseUrl}>
                <Input name="baseUrl" label={providerDef()?.requiresBaseUrl ? 'Base URL (obligatoria)' : 'Base URL (opcional)'} placeholder={providerDef()?.defaultBaseUrl ?? 'https://…'} />
              </Show>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <div class="flex justify-end gap-2">
            <Button variant="text" color="default" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button type="submit" form="connection-form" disabled={form.submitting() || !form.valid()}>
              {form.submitting() ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <alert.AlertComponent />
    </Card>
  )
}

export default AIConnectionsManager
